// Public UI adapted from Klargo-Reference/components/landing/HeroArtifacts.tsx.
import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../layout/LanguageSwitcher.jsx';
import RegistrationPlate from '../common/RegistrationPlate.jsx';
import { ForwardArrow, VerifiedTick } from '../ds/StatusMark.jsx';
import { formatDateLocalized } from '../../lib/format.jsx';
import HeroDesk from './HeroDesk.jsx';
import { HERO_DEAL, INSURANCE_PROVIDERS } from './heroDeal.jsx';
export { HERO_DEAL } from './heroDeal.jsx';
const HeroCard = ({ context, chip, children }) => (<div className="hero-card-sheet flex h-full w-full flex-col overflow-hidden rounded-[12px] border border-ds-rule-edge bg-ds-sheet p-[18px]">
    <header data-chip={chip ? 'true' : 'false'} className="flex h-[22px] flex-none items-center justify-between gap-2.5">
      <span className="hero-card-context text-[11.5px] font-semibold tracking-[0.02em] text-ds-muted">
        {context}
      </span>
      {chip && (<span className="inline-flex flex-none items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-[3px] text-[11px] font-semibold" style={{ background: 'var(--ds-green-bg)', color: 'var(--ds-green-deep)' }}>
          <VerifiedTick />
          {chip}
        </span>)}
    </header>
    {children}
  </div>);
const RoleLabel = ({ children }) => (<div className="text-[10px] font-semibold uppercase tracking-[0.05em] text-ds-body">
    {children}
  </div>);
const ContractBody = () => {
    const { t, languageCode } = useLanguage();
    const signedDate = formatDateLocalized(HERO_DEAL.dateIso, languageCode);
    const parties = [
        { role: t('Säljare'), name: HERO_DEAL.seller },
        { role: t('Köpare'), name: HERO_DEAL.buyer },
    ];
    return (<>
      <div className="mt-1.5 font-ds-serif text-[17px] font-semibold leading-[1.2] text-ds-ink">
        {t('Köpekontrakt')}
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <RegistrationPlate value={HERO_DEAL.reg} size="micro"/>
        <span className="truncate text-[12px] font-medium text-ds-body">{HERO_DEAL.model}</span>
      </div>

      <div className="mt-2.5 grid flex-none grid-cols-2 gap-x-3 border-t-2 border-ds-ink pt-[7px]">
        {parties.map((party) => (<div key={party.role}>
            <div className="text-[10px] font-semibold tracking-[0.05em] text-ds-muted">
              {party.role.toUpperCase()}
            </div>
            <div className="mt-px truncate text-[12.5px] font-semibold leading-[1.25] text-ds-ink">
              {party.name}
            </div>
          </div>))}
      </div>

      <div className="mt-[7px] flex items-baseline justify-between gap-3 border-t border-ds-rule-row pt-[6px]">
        <span className="text-[11.5px] text-ds-muted">{t('Pris')}</span>
        <span className="text-[13.5px] font-semibold tabular-nums text-ds-ink">
          {HERO_DEAL.price}
        </span>
      </div>

      
      <div className="mt-[7px] border-t border-ds-rule-row">
        {[
            { heading: t('contract.preview.condition'), body: t('contract.preview.condition.body1') },
            { heading: t('contract.preview.loan'), body: t('contract.preview.loan.body2') },
        ].map((clause) => (<div key={clause.heading} className="border-b border-ds-rule-row py-[5px]">
            <div className="text-[11.5px] font-semibold leading-[1.3] text-ds-ink">
              {clause.heading}
            </div>
            <div className="hero-clause-body mt-px line-clamp-1 text-[11px] leading-[1.4] text-ds-muted">
              {clause.body}
            </div>
          </div>))}
      </div>

      
      <div className="mt-auto grid flex-none grid-cols-2 gap-x-4 pt-2">
        {parties.map((party) => (<div key={party.role} className="border-t border-[rgba(20,36,46,.42)] pt-[4px]">
            <div className="truncate font-ds-serif text-[12.5px] font-semibold leading-[1.2] text-ds-ink">
              {party.name}
            </div>
            <div className="text-[10px] text-ds-muted">{party.role}</div>
          </div>))}
      </div>
      <div className="mt-[6px] flex flex-none items-center gap-1.5 text-[11px] text-ds-muted">
        <VerifiedTick />
        <span className="truncate">
          {t('Signerat med BankID')} · {signedDate}
        </span>
      </div>
    </>);
};
const PaymentBody = () => {
    const { t, languageCode } = useLanguage();
    const confirmedDate = formatDateLocalized(HERO_DEAL.dateIso, languageCode);
    return (<>
      
      <div className="mt-2.5 flex-none">
        <div className="hero-pay-amount font-ds-serif text-[27px] font-semibold leading-[1.05] tracking-[-0.01em] text-ds-ink">
          {HERO_DEAL.price}
        </div>
        <div className="mt-[3px] text-[11.5px] leading-[1.4] text-ds-muted">
          {t('Betalt med Swish')}
        </div>
      </div>

      
      <div className="mt-3.5 grid flex-none grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-2 border-t border-ds-rule-row pt-[9px]">
        <div>
          <RoleLabel>{t('Köpare')}</RoleLabel>
          <div className="mt-px truncate text-[12.5px] font-semibold leading-[1.25] text-ds-ink">
            {HERO_DEAL.buyer}
          </div>
        </div>
        <ForwardArrow stroke="var(--ds-muted)" width={14} height={10}/>
        <div className="text-right">
          <RoleLabel>{t('Säljare')}</RoleLabel>
          <div className="mt-px truncate text-[12.5px] font-semibold leading-[1.25] text-ds-ink">
            {HERO_DEAL.seller}
          </div>
        </div>
      </div>

      
      <div className="hero-pay-foot mt-auto flex flex-none flex-col items-end border-t border-ds-rule-row pt-[7px] text-right">
        
        <div className="hero-pay-confirm flex flex-col items-end">
          <div className="hero-pay-state flex items-center gap-1.5 text-[11px] leading-[1.4] text-ds-muted">
            <VerifiedTick />
            <span>{t('Bekräftad')}</span>
          </div>
          <div className="text-[11px] leading-[1.4] text-ds-muted">{confirmedDate}</div>
        </div>
        <div className="hero-pay-ref mt-[5px]">
          <RoleLabel>{t('Affärsreferens')}</RoleLabel>
          <div className="mt-px font-ds-mono text-[11px] tracking-[0.02em] text-ds-ink">
            {HERO_DEAL.contractNumber}
          </div>
        </div>
      </div>
    </>);
};
const TransferBody = () => {
    const { t } = useLanguage();
    return (<>
      
      <div className="mt-3 flex-none">
        <RegistrationPlate value={HERO_DEAL.reg} size="row"/>
      </div>
      <div className="mt-2 text-[11.5px] font-medium leading-[1.4] text-ds-muted">
        {HERO_DEAL.model}
      </div>

      
      <div className="mt-2.5 flex-none border-t-2 border-ds-ink pt-[8px]">
        <div className="font-ds-serif text-[16.5px] font-semibold leading-[1.25] text-ds-ink">
          {HERO_DEAL.buyer} {t('blir ny ägare')}
        </div>
        <div className="mt-[5px] text-[12px] leading-[1.4] text-ds-body">
          {t('Underlaget är förberett')}
        </div>
      </div>

      
      <div className="hero-transfer-gap mt-12 flex-none border-t border-ds-rule-row pt-[8px]">
        <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-ds-body">
          {t('Nästa steg')}
        </div>
        <div className="mt-[4px] text-[12.5px] leading-[1.45] text-ds-body">
          {t('Du anmäler ägarbytet hos Transportstyrelsen.')}
        </div>
      </div>

      
      <div className="hero-object-foot mt-auto flex-none border-t border-ds-rule-row pt-[8px]">
        <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-ds-text">
          <span>{t('Öppna Transportstyrelsen')}</span>
          <ForwardArrow stroke="var(--ds-muted)" width={12} height={9}/>
        </span>
      </div>
    </>);
};
const InsuranceBody = () => {
    const { t } = useLanguage();
    return (<>
      
      <div className="mt-1.5 flex-none">
        <div className="font-ds-serif text-[17px] font-semibold leading-[1.2] text-ds-ink">
          {t('Jämför försäkring')}
        </div>
        <div className="mt-[3px] text-[11.5px] leading-[1.4] text-ds-muted">
          {t('Förmedlas via Zmarta')}
        </div>
      </div>

      
      <div className="mt-2.5 flex-none border-t border-ds-rule-row pt-[10px]">
        
        <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.05em] text-ds-muted">
          {t('hero.insurers.exampleLabel')}
        </div>
        
        <div className="space-y-[18px]">
          {INSURANCE_PROVIDERS.map((companyKey) => (<div key={companyKey} className="text-[12.5px] font-semibold leading-[1.3] text-ds-ink">
              {t(companyKey)}
            </div>))}
        </div>
      </div>



      
      <div className="hero-object-foot mt-auto flex-none border-t border-ds-rule-row pt-[8px]">
        <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-ds-text">
          <span>{t('Jämför och teckna')}</span>
          <ForwardArrow stroke="var(--ds-muted)" width={12} height={9}/>
        </span>
      </div>
    </>);
};
const CardByKey = ({ cardKey }) => {
    const { t } = useLanguage();
    switch (cardKey) {
        case 'contract':
            return (<HeroCard context={t('Kontrakt')} chip={t('Signerat')}>
          <ContractBody />
        </HeroCard>);
        case 'payment':
            return (<HeroCard context={t('Betalning')}>
          <PaymentBody />
        </HeroCard>);
        case 'transfer':
            return (<HeroCard context={t('Ägarbyte')}>
          <TransferBody />
        </HeroCard>);
        case 'insurance':
            return (<HeroCard context={t('Försäkring')}>
          <InsuranceBody />
        </HeroCard>);
        default:
            return null;
    }
};
const Caption = ({ className }) => {
    const { t } = useLanguage();
    return (<p className={`mb-0 mt-4 text-[12px] leading-[1.5] text-ds-muted ${className ?? ''}`}>
      {t('Exempel från en affär i Klargo.')}
    </p>);
};
const FAN = [
    { key: 'transfer', tilt: '-4deg', gx: '-6px', gy: '-4px', left: 0, top: 0, z: 1 },
    { key: 'payment', tilt: '4deg', gx: '6px', gy: '-3px', left: 314, top: 12, z: 2 },
    { key: 'insurance', tilt: '-3deg', gx: '-6px', gy: '4px', left: -8, top: 178, z: 3 },
    { key: 'contract', tilt: '-1deg', gx: '0px', gy: '3px', left: 176, top: 166, z: 10 },
];
const HeroFan = () => {
    const [activeKey, setActiveKey] = useState(null);
    const fanRef = useRef(null);
    useEffect(() => {
        if (!activeKey)
            return;
        const release = (event) => {
            if (fanRef.current && !fanRef.current.contains(event.target)) {
                setActiveKey(null);
            }
        };
        document.addEventListener('pointerdown', release);
        return () => document.removeEventListener('pointerdown', release);
    }, [activeKey]);
    const onCardPointerDown = (event, key) => {
        const pointerType = event.pointerType || event.nativeEvent.pointerType;
        if (pointerType !== 'touch')
            return;
        setActiveKey((current) => (current === key ? null : key));
    };
    return (<div ref={fanRef} aria-hidden="true" className="hero-fan mx-auto" data-touch={activeKey ? 'true' : undefined}>
    {FAN.map((card) => (<div key={card.key} className="hero-card" data-active={activeKey === card.key ? 'true' : undefined} onPointerDown={(event) => onCardPointerDown(event, card.key)} style={{
                left: card.left,
                top: card.top,
                '--z': card.z,
                '--tilt': card.tilt,
                '--gx': card.gx,
                '--gy': card.gy,
            }}>
        <CardByKey cardKey={card.key}/>
      </div>))}
  </div>);
};
const HeroArtifacts = ({ mobileFooter }) => (<div>
    
    <div className="hidden md:block">
      <HeroFan />
      <Caption className="text-center min-[1280px]:text-left"/>
    </div>
    
    <div className="md:hidden">
      <HeroDesk footer={mobileFooter}/>
    </div>
  </div>);
export default HeroArtifacts;
